import { z } from 'zod';
import type { FlowAccountIntel } from '@shared/flow/account-intel';

/**
 * Pure parser for the account-level Keystone intelligence blob, lifted out
 * of `AccountIntelAllView.tsx` (bead terra-design-v2-drilldowns-ppn.4, U1d,
 * plan 005 §2). `AccountIntelAllView` now renders from `parseAccountIntelBlob`
 * instead of the inline `as` casts + defensive helpers this file used to
 * carry — moved verbatim, JSX/test-ids unchanged.
 *
 * The blob at `state.state` is `z.unknown()` on the wire
 * (`src/shared/flow/account-intel.ts:28`) because it drifts across Keystone
 * generator versions, so every field below is read defensively — never a
 * bare `as` cast for anything a schema already covers (Do NOT Create list).
 */

// ── Defensive helpers (the blob is z.unknown on the wire) ────────────────────

/** Coerces a mixed-shape value to a display string, or `null` when there's
 * nothing to show. An object collapses to its own populated values joined
 * with `' · '` (mirrors how a denormalized "meta" object was rendered before
 * this lift). */
export function safeString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const vals = Object.values(value as Record<string, unknown>).filter(v => v != null && v !== '');
    return vals.length > 0 ? vals.map(v => (typeof v === 'string' ? v : String(v))).join(' · ') : null;
  }
  // Remaining types (bigint/symbol/function) aren't meaningful intel values.
  return null;
}

/** Signal lists arrive as string[] or {text}[] — normalize to string[],
 * dropping blank strings and entries with neither a string form nor a
 * `text` property. */
export function normalizeSignalItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'text' in item) return String((item as { text: unknown }).text);
      return null;
    })
    .filter((s): s is string => s != null && s.trim() !== '');
}

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtCurrency(value: number | null | undefined): string | null {
  if (value == null) return null;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value}`;
}

export type Severity = 'red' | 'amber' | 'gray';
export function severityColor(severity: string | undefined): Severity {
  if (!severity) return 'gray';
  const s = severity.toLowerCase();
  if (s === 'high' || s === 'critical') return 'red';
  if (s === 'medium') return 'amber';
  return 'gray';
}

// ── Blob item schemas (best-effort shapes; every access is guarded) ─────────
//
// `state.state` is `z.unknown()` on the wire — a Keystone generator-version
// drift can hand this a non-string `severity`/`priority` (e.g. a number), a
// non-array `pipeline`, or a null array element. `risks`/`opportunities`/
// `deal.pipeline` are array-of-object shapes that need their own lenient
// parse so a bad entry is dropped or coerced instead of throwing.

/** A string field that coerces any non-string (including null) to
 * `undefined` rather than failing the whole item — callers already
 * falsy-guard `undefined`. */
const lenientOptionalString = z.string().optional().catch(undefined);

export const RiskItemSchema = z.object({
  risk: lenientOptionalString,
  severity: lenientOptionalString,
  mitigation: lenientOptionalString,
});
export type RiskItem = z.infer<typeof RiskItemSchema>;

export const OpportunityItemSchema = z.object({
  opportunity: lenientOptionalString,
  priority: lenientOptionalString,
});
export type OpportunityItem = z.infer<typeof OpportunityItemSchema>;

export const PipelineItemSchema = z.object({
  name: lenientOptionalString,
  stage: lenientOptionalString,
  amount: z.number().optional().catch(undefined),
});
export type PipelineItem = z.infer<typeof PipelineItemSchema>;

/**
 * Parses an unknown value as an array of `itemSchema`, dropping (not
 * throwing on) any element that isn't a real object matching that schema.
 * Covers both "the whole field isn't an array" (a truthy non-array, e.g. a
 * string or object) and "an individual element is null/malformed" — both
 * observed drift shapes for this blob.
 */
export function parseLenientArray<T>(items: unknown, itemSchema: z.ZodType<T>): T[] {
  if (!Array.isArray(items)) return [];
  const out: T[] = [];
  for (const item of items) {
    const parsed = itemSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

// ── Public derived view ──────────────────────────────────────────────────────

export interface AccountIntelQuickStats {
  icpGrade: string | null;
  buyingIntent: string | null;
  relationshipHealth: string | null;
  employees: number | undefined;
  segment: string | undefined;
  industry: string | undefined;
  champion: string | null;
  econBuyer: string | null;
  icpReasoning: string | undefined;
}

export interface AccountIntelSignals {
  positive: string[];
  negative: string[];
  risks: RiskItem[];
  opportunities: OpportunityItem[];
}

export interface AccountIntelRelationship {
  health?: string;
  momentum?: string;
  touchpointFrequency?: string;
  lastContactDate?: string;
  keyRelationships?: unknown[];
}

export interface AccountIntelDeal {
  primaryDeal?: { stage?: string; amount?: number; closeDate?: string; name?: string } | null;
  pipeline: PipelineItem[];
  history?: string;
}

export interface AccountIntelView {
  accountFocus: { type?: string; strategy?: string } | null;
  quickStats: AccountIntelQuickStats;
  signals: AccountIntelSignals;
  relationship: AccountIntelRelationship | null;
  deal: AccountIntelDeal;
  fitScoreSummary: string | null;
  propensityScoreReasoning: string | null;
  generatedAt: string | null;
  /** Formatted `generatedAt`, matching `AccountIntelAllView`'s "Generated …"
   * footer (`fmtDate(outer.generatedAt)`). */
  generatedAtDisplay: string | null;
  hasContent: boolean;
}

/**
 * Parses the full `FlowAccountIntel` envelope into the derived view
 * `AccountIntelAllView` (and, going forward, U4's `AccountIntelBody`) render
 * from — exactly the derivations previously inlined at
 * `AccountIntelAllView.tsx:175-207`.
 */
export function parseAccountIntelBlob(data: FlowAccountIntel): AccountIntelView {
  const outer = data.state;
  const inner = (outer.state ?? {}) as Record<string, unknown>;

  const accountFocus = (inner.accountFocus ?? null) as { type?: string; strategy?: string } | null;
  const relationship = (inner.relationship ?? null) as AccountIntelRelationship | null;
  const dealRaw = inner.deal as {
    primaryDeal?: { stage?: string; amount?: number; closeDate?: string; name?: string } | null;
    pipeline?: unknown;
    history?: string;
  } | null | undefined;
  const profile = inner.profile as { size?: number; segment?: string; industry?: string; icpFit?: { reasoning?: string } } | null | undefined;

  const signalsRaw = inner.signals as Record<string, unknown> | null | undefined;
  const signalSummary = (signalsRaw?.summary ?? {}) as Record<string, unknown>;
  const positive = normalizeSignalItems(signalSummary.positive ?? signalsRaw?.positive);
  const negative = normalizeSignalItems(signalSummary.negative ?? signalsRaw?.negative);
  const risks = parseLenientArray(signalsRaw?.risks, RiskItemSchema);
  const opportunities = parseLenientArray(signalsRaw?.opportunities, OpportunityItemSchema);
  const pipeline = parseLenientArray(dealRaw?.pipeline, PipelineItemSchema);

  const deal: AccountIntelDeal = {
    primaryDeal: dealRaw?.primaryDeal,
    pipeline,
    history: dealRaw?.history,
  };

  const quickStats: AccountIntelQuickStats = {
    icpGrade: outer.icpGrade,
    buyingIntent: outer.buyingIntent,
    relationshipHealth: outer.relationshipHealth,
    employees: profile?.size,
    segment: profile?.segment,
    industry: profile?.industry,
    champion: outer.hasChampion == null ? null : outer.hasChampion ? 'Yes' : 'No',
    econBuyer: outer.hasEconomicBuyer == null ? null : outer.hasEconomicBuyer ? 'Yes' : 'No',
    icpReasoning: profile?.icpFit?.reasoning,
  };

  const hasSignals = positive.length + negative.length + risks.length + opportunities.length > 0;
  const hasQuickStats = Boolean(outer.icpGrade || outer.buyingIntent || outer.relationshipHealth) || profile?.size != null;
  const hasContent = Boolean(
    accountFocus
    || hasSignals
    || hasQuickStats
    || (relationship && (relationship.health || relationship.momentum || relationship.touchpointFrequency))
    || (dealRaw && (dealRaw.primaryDeal || pipeline.length > 0 || dealRaw.history))
    || outer.fitScoreSummary
    || outer.propensityScoreReasoning,
  );

  return {
    accountFocus,
    quickStats,
    signals: { positive, negative, risks, opportunities },
    relationship,
    deal,
    fitScoreSummary: outer.fitScoreSummary ?? null,
    propensityScoreReasoning: outer.propensityScoreReasoning ?? null,
    generatedAt: outer.generatedAt,
    generatedAtDisplay: fmtDate(outer.generatedAt),
    hasContent,
  };
}
